import React, { useContext, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { NumericFormat } from "react-number-format";
import * as _ from "lodash-es";
import IOwTableCols from "@models/IOwTableCols";
import IUserUI from "@models/IUserUI";
import ISupplierModel from "@models/ISupplierModel";
import ICatalog from "@models/ICatalog";
import ICatalog_ItemUI from "@models/ICatalog_ItemUI";
import IPrice_Group from "@models/IPrice_Group";
import { OwDeleteEditActions } from "@components/shared/OwDeleteEditActions/OwDeleteEditActions";
import OwButton from "@components/shared/OwButton/OwButton";
import OwS3ImageViewer from "@components/shared/OwS3ImageViewer/OwS3ImageViewer";
import IImageSizeEnum from "@models/IImageSizeEnum";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwPill from "@components/shared/OwPill/OwPill";
import IntegrationsConnectedBar from "@components/shared/IntegrationsConnectedBar/IntegrationsConnectedBar";
import { PriceBandPriceDisplay } from "@components/shared/PriceBandPriceDisplay/PriceBandPriceDisplay";
import HelperService from "@services/HelperService";
import { CatalogOverviewState } from "./useCatalogOverview";
import wayfairIcon from "@assets/images/wayfair/wayfair-icon.svg";
import UserContext from "@contexts/UserContext";
import SupplierContext from "@contexts/SupplierContext";
import SortOrderEnum from "@models/SortOrderEnum";
import CatalogService from "@services/CatalogService";
import OrderEntryService from "@services/OrderEntryService";

interface UseTableColumnsProps {
    state: CatalogOverviewState;
    setState: (updates: Partial<CatalogOverviewState>) => void;
    catalogModel?: ICatalog;
    openCatalogItemSidebar: (catalog_item: any, index?: number) => void;
    cloneItem: (row: ICatalog_ItemUI, index: number) => void;
}

export const useCatalogTableColumns = ({ state, setState, catalogModel, openCatalogItemSidebar, cloneItem }: UseTableColumnsProps) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const columns = useMemo(() => {
        const toggleConfirmDelete = (row: ICatalog_ItemUI, index: number) => {
            const sourceElem = document.getElementById(`row-${index}`);
            setState({
                sourceElem,
                sourceCatalogItem: row,
                deleteElem: `row-${index}`,
            });
        };

        const renderPhotoCol = (row: ICatalog_ItemUI, index: number) => {
            return state.isPhotoView ? (
                <div>
                    <OwS3ImageViewer
                        catalogItem={row}
                        isMicroView={true}
                        attachmentIndex={0}
                        imageIndex={IImageSizeEnum.THUMB}
                        className={"me-2 mb-2 mb-md-0"}
                        includeAllImages={true}
                        hideEmptyImage={true}
                    />
                </div>
            ) : null;
        };

        const renderActionsCol = (row: ICatalog_ItemUI, index: number) => {
            let hasMainImage = false;
            let hasFiles = false;
            let hasProductGroup = false;
            let hasAlternateSourcing = false;
            let hasWarehouses = false;
            let hasWayfair = false;
            let readOnly = false;

            for (const attachment of row.Attachments || []) {
                if (attachment.image_seq === 1) {
                    hasMainImage = true;
                }
            }

            if (row?.is_product_group) {
                hasProductGroup = true;
            }

            if (row?.Attachments?.length) {
                hasFiles = true;
            }

            if (row?.has_alternate_sourcing) {
                hasAlternateSourcing = true;
            }

            if ((row?.Warehouse_Catalog_Items_Maps || []).length) {
                hasWarehouses = true;
            }

            if (row.wayfair_item) {
                hasWayfair = true;
            }

            if (supplierModel.settings) {
                if (!_.isString(supplierModel.settings)) {
                    if (supplierModel.settings.screen_options?.catalogs) {
                        if (
                            userModel.supplierUserToken?.user_type_id &&
                            // @ts-expect-error
                            !supplierModel.settings.screen_options.catalogs[userModel.supplierUserToken.user_type_id].EDIT
                        ) {
                            readOnly = true;
                        }
                    }
                }
            }

            if (readOnly) {
                return null;
            }

            return (
                <div className={"info-icons d-flex align-items-center"}>
                    <span className={"state-circle me-4 " + (row.isSaved ? "bluish-green-bg" : "red-bg")} />
                    <OwDeleteEditActions
                        isEditMode={false}
                        onEditClickFn={(e) => {
                            openCatalogItemSidebar(row, index);
                        }}
                        onTrashClickFn={() => {
                            toggleConfirmDelete(row, index);
                        }}
                    />
                    <span className="ms-2 me-2">
                        <OwButton
                            centerIconClass="fad fa-clone"
                            fn={() => {
                                cloneItem(row, index);
                            }}
                            hoverTitle="Clone"
                        />
                    </span>
                    <span className={"col-auto .g-0 item-has-image"}>
                        <i title={"Has Image?"} className={"far fa-image ms-md-4 " + (hasMainImage ? "text-warning" : "text-muted")}></i>
                    </span>
                    <span className={"col-auto .g-0 item-has-spec-file"}>
                        <i title={"Files?"} className={"fad fa-file-alt ms-md-2 " + (hasFiles ? "text-primary" : "text-muted")}></i>
                    </span>
                    {!supplierModel.has_warehouses ? null : (
                        <span className={"col-auto .g-0 item-has-warehouses"}>
                            <i
                                title={"Warehouses?"}
                                className={"fad fa-warehouse ms-md-2 " + (hasWarehouses ? "text-primary" : "text-muted")}></i>
                        </span>
                    )}
                    {supplierModel.has_product_sources && !row.is_alternate_source ? (
                        <span className={"item-has-alternate-sourcing"}>
                            <i
                                className={"fad fa-share-alt ms-md-2 " + (hasAlternateSourcing ? "text-primary" : "text-muted")}
                                title={"Has Alternate Sourcing?"}
                            />
                        </span>
                    ) : null}
                    {supplierModel.has_product_sources && row.is_alternate_source ? (
                        <span className={"item-is-alternate-source"}>
                            <i className={"fad fa-exchange-alt ms-md-2 text-primary"} title={"Is Alternate Source"} />
                        </span>
                    ) : null}
                    {hasWayfair ? (
                        <span className={"ms-1 col-auto .g-0"}>
                            <img src={wayfairIcon} title={"Wayfair Item"} height={"16"} />
                        </span>
                    ) : null}
                    {supplierModel.has_child_items ? (
                        <span className={""}>
                            <i
                                className={"fa-duotone fa-shapes ms-1 " + (row.has_child_items ? "text-info" : "text-muted")}
                                title={"Child Items?"}
                            />
                        </span>
                    ) : null}
                    {row.Item_Options?.length ? (
                        <span>
                            <i className={"fad fa-list-check ms-2 text-primary"} title={"Item Options"} />
                        </span>
                    ) : null}
                    {supplierModel.has_serials ? (
                        <span>
                            <i
                                className={"ms-2 fas fa-barcode-alt " + (row.has_serials ? "text-warning" : "text-muted")}
                                title="Has Serials"
                            />
                        </span>
                    ) : null}
                </div>
            );
        };

        const renderActiveCol = (active: boolean) => {
            return (
                <div className={"text-center " + (active ? "bluish-green" : "text-danger")}>
                    <span className={"fad fa-check-circle"} />
                </div>
            );
        };

        const renderCategoryCol = (row: ICatalog_ItemUI, index: number) => {
            let categoryStr: any = ``;

            if (row.CategoryHierarchy) {
                categoryStr = row.CategoryHierarchy.map((category, index) => {
                    if (index === row.CategoryHierarchy!.length - 1) {
                        return <span key={category.guid}>{category.name}</span>;
                    } else {
                        return (
                            <span key={category.guid}>
                                {category.name} <i className={"far fa-arrow-right fa-xs"} />{" "}
                            </span>
                        );
                    }
                });
            }

            return <div>{categoryStr}</div>;
        };

        const renderTypeCol = (row: ICatalog_ItemUI, index: number) => {
            let typeStr: any = ``;

            if (row.is_kit) {
                typeStr = "kit";
            } else if (row.is_product_group) {
                typeStr = "product_group";
            } else if (row.has_inventory) {
                typeStr = "inventory";
            } else {
                typeStr = "non_inventory";
            }

            let leftIconClass = "";

            if (row.is_kit && row.is_advanced_kit && !row.is_kit_builder) {
                leftIconClass = "fad fa-flask text-success";
            } else if (row.is_kit && row.is_kit_builder) {
                leftIconClass = "fad fa-flask-gear text-warning";
            }

            return (
                <div>
                    <OwLabel title={typeStr} leftIconClass={leftIconClass} />
                </div>
            );
        };

        const renderSkuCol = (row: ICatalog_ItemUI) => {
            return (
                <div>
                    <div>{row.sku}</div>
                    {row.is_ordered_on_demand ? <OwPill title={"ordered_on_demand"} className={"text-small no-wrap"} /> : null}
                </div>
            );
        };

        const renderInventoryCol = (row: ICatalog_ItemUI) => {
            if (row.has_inventory) {
                return (
                    <table className="table text-end">
                        <tr>
                            <td>{state.extendedInventoryView && <>Qty. On Hand:</>}</td>
                            <td>{row.qty_on_hand}</td>
                        </tr>

                        {state.extendedInventoryView && (
                            <>
                                <tr>
                                    <td>Qty Incoming:</td>
                                    <td>{row.qty_incoming}</td>
                                </tr>
                                <tr>
                                    <td>Qty Backordered:</td>
                                    <td>{row.qty_backordered}</td>
                                </tr>
                                <tr>
                                    <td>Qty Reserved:</td>
                                    <td>{row.reserved_qty_on_hand || 0}</td>
                                </tr>
                                <tr>
                                    <td>Qty. Held For Backorders:</td>
                                    <td>{row.qty_held_for_backorders}</td>
                                </tr>
                                <tr>
                                    <td>Allow Backorder:</td>
                                    <td>{row.allow_backorder ? "Yes" : "No"}</td>
                                </tr>
                            </>
                        )}
                    </table>
                );
            } else {
                return null;
            }
        };

        const renderShortDescriptionCol = (row: ICatalog_ItemUI) => {
            if (row.short_description) {
                return <div className={"pre-wrap"} dangerouslySetInnerHTML={{ __html: row.short_description }}></div>;
            } else {
                return null;
            }
        };

        const renderDescriptionCol = (row: ICatalog_ItemUI) => {
            if (row.description) {
                return <div className={"pre-wrap"} dangerouslySetInnerHTML={{ __html: row.description }}></div>;
            } else {
                return null;
            }
        };

        const renderMsrpCol = (row: ICatalog_ItemUI) => {
            if (row.msrp && _.isNumber(row.msrp)) {
                let prefix = "$";

                if (catalogModel?.currency_id) {
                    prefix = CatalogService.getCurrencyFromId(catalogModel.currency_id);
                }

                return (
                    <NumericFormat
                        value={Number(row.msrp)}
                        displayType={"text"}
                        thousandSeparator={true}
                        decimalScale={2}
                        fixedDecimalScale={true}
                        prefix={prefix}
                    />
                );
            }
        };

        const renderMapCol = (row: ICatalog_ItemUI) => {
            if (row.map) {
                let prefix = "$";

                if (catalogModel?.currency_id) {
                    prefix = CatalogService.getCurrencyFromId(catalogModel.currency_id);
                }

                return (
                    <NumericFormat
                        value={Number(row.map)}
                        displayType={"text"}
                        thousandSeparator={true}
                        decimalScale={2}
                        fixedDecimalScale={true}
                        prefix={prefix}
                    />
                );
            }
        };

        const renderPriceGroupCol = (row: ICatalog_ItemUI, index: number, priceGroup: IPrice_Group) => {
            const pricing = row.pricing;

            let currencyName = catalogModel?.Currency?.name ? catalogModel.Currency.name : "USD";

            if (catalogModel?.currency_id) {
                currencyName = CatalogService.getCurrencyNameFromId(catalogModel.currency_id);
            }

            // A sum-priced basic kit has no price of its own: show the API-computed component sum, marked Σ,
            // rather than its (ignored) pricing JSON.
            if (OrderEntryService.isSumComponentPricedKit(row)) {
                const kitSum = row.kit_sum_pricing?.[priceGroup.value];

                return (
                    <FormattedMessage id={"kit_sum_component_pricing"}>
                        {(label) => (
                            <div className={"text-nowrap"} title={String(label)}>
                                <span className={"me-1"}>&Sigma;</span>
                                {!_.isNil(kitSum)
                                    ? HelperService.formatCurrency({
                                          value: Number(kitSum),
                                          currency: currencyName,
                                          precision: supplierModel.pricing_precision || 2,
                                      })
                                    : null}
                            </div>
                        )}
                    </FormattedMessage>
                );
            }

            if (pricing) {
                const priceGroupValue = pricing[priceGroup.value] || 0;

                if (priceGroupValue === "N/A") {
                    return <div>N/A</div>;
                } else {
                    if (row.has_price_bands) {
                        return (
                            <PriceBandPriceDisplay
                                bands={(row.Price_Bands || {})[priceGroup.value] || []}
                                currency={catalogModel?.Currency}
                            />
                        );
                    } else {
                        return HelperService.formatCurrency({
                            value: Number(priceGroupValue),
                            currency: currencyName,
                            precision: supplierModel.pricing_precision || 2,
                        });
                    }
                }
            } else {
                return <div></div>;
            }
        };

        const renderUnitOfMeasurementCol = (row: ICatalog_ItemUI) => {
            if (row.Base_Unit) {
                return _.get(row, "Base_Unit.unit_abbr", "");
            } else {
                return _.get(row, "Unit_Of_Measurement.abbreviation", "");
            }
        };

        const renderIntegrationsCol = (row: ICatalog_ItemUI) => {
            return (
                <IntegrationsConnectedBar
                    squareConnected={false}
                    shipstationConnected={false}
                    zohoConnected={false}
                    wooCommerceConnected={row.woocommerce_product_id}
                    supplierModel={supplierModel}
                    qboConnected={row.qbo_item_id}
                    qbdConnected={!!row.qbd_ListID}
                    shopifyConnected={!!row.shopify_id}
                    fishbowlConnected={!!row.fishbowl_id}
                    openCartConnected={!!row.opencart_product_id}
                    xeroConnected={!!row.xero_item_id}
                    position={"center"}
                    showIntegrations={{
                        quickbooksDesktop: true,
                        quickbooksOnline: true,
                        shipstation: false,
                        square: true,
                        zoho: false,
                        wooCommerce: true,
                        shopify: true,
                        fishbowl: true,
                        opencart: true,
                        xero: true,
                    }}
                />
            );
        };

        const toggleSortDir = (sortCol: string, sortOrder: SortOrderEnum) => {
            setState({ sortDir: sortOrder, sortCol });
        };

        const tableColumns: IOwTableCols[] = [
            {
                title: "",
                valueProp: "",
                isActionsCol: true,
                renderFn: renderActionsCol,
                renderFnEmitsRow: true,
                className: "w-15r",
            },
        ];

        if (state.columns.active.isChecked) {
            tableColumns.push({
                title: "active",
                valueProp: "active",
                renderFn: renderActiveCol,
                className: "text-center",
            });
        }

        if (state.columns.category.isChecked) {
            tableColumns.push({
                title: "category",
                valueProp: "category",
                renderFn: renderCategoryCol,
                renderFnEmitsRow: true,
                className: "text-center",
                sortOrderFn: () => state.sortDir,
                sortFn: toggleSortDir,
                isSortable: true,
            });
        }

        if (state.columns.type.isChecked) {
            tableColumns.push({
                title: "type",
                valueProp: "type",
                renderFn: renderTypeCol,
                renderFnEmitsRow: true,
                className: "text-center",
                isSortable: false,
            });
        }

        tableColumns.push({
            renderFn: renderPhotoCol,
            renderFnEmitsRow: true,
            title: "",
            valueProp: "",
            isChecked: () => state.isPhotoView,
        });

        if (state.columns.name.isChecked) {
            tableColumns.push({
                renderFnEmitsRow: true,
                title: "name",
                valueProp: "name",
                sortOrderFn: () => state.sortDir,
                sortFn: toggleSortDir,
                isSortable: true,
            });
        }

        if (state.columns.sku.isChecked) {
            tableColumns.push({
                title: "sku",
                valueProp: "sku",
                renderFn: renderSkuCol,
                renderFnEmitsRow: true,
                sortOrderFn: () => state.sortDir,
                sortFn: toggleSortDir,
                isSortable: true,
            });
        }

        if (state.columns.inventory.isChecked) {
            tableColumns.push({
                title: "inventory",
                valueProp: "qty_on_hand",
                isSortable: false,
                renderFn: renderInventoryCol,
                renderFnEmitsRow: true,
            });
        }

        if (state.columns.short_description.isChecked) {
            tableColumns.push({
                title: "short_description",
                valueProp: "short_description",
                renderFn: renderShortDescriptionCol,
                renderFnEmitsRow: true,
            });
        }

        if (state.columns.description.isChecked) {
            tableColumns.push({
                title: "description",
                valueProp: "description",
                renderFn: renderDescriptionCol,
                renderFnEmitsRow: true,
            });
        }

        if (state.columns.msrp.isChecked) {
            tableColumns.push({
                title: "msrp",
                valueProp: "msrp",
                className: "text-center",
                renderFn: renderMsrpCol,
                renderFnEmitsRow: true,
            });
        }

        if (state.columns.map.isChecked && supplierModel.has_map) {
            tableColumns.push({
                title: "MAP",
                valueProp: "map",
                className: "text-center",
                renderFn: renderMapCol,
                renderFnEmitsRow: true,
            });
        }

        if (state.priceGroups && state.columns.pricing.isChecked) {
            for (const priceGroup of state.priceGroups) {
                tableColumns.push({
                    title: _.startCase(priceGroup.value),
                    valueProp: "",
                    skipTitleTranslate: true,
                    renderFn: (row: ICatalog_ItemUI, rowIndex: number) => {
                        return renderPriceGroupCol(row, rowIndex, priceGroup);
                    },
                    renderFnEmitsRow: true,
                    className: "text-left",
                });
            }
        }

        if (state.columns.uom.isChecked) {
            tableColumns.push({
                title: "uom",
                valueProp: "",
                renderFn: renderUnitOfMeasurementCol,
                renderFnEmitsRow: true,
                className: "text-center",
            });
        }

        if (state.columns.integrations.isChecked) {
            tableColumns.push({
                title: "integrations",
                valueProp: "",
                renderFn: renderIntegrationsCol,
                renderFnEmitsRow: true,
            });
        }

        return tableColumns;
    }, [
        state.columns,
        state.priceGroups,
        state.isPhotoView,
        state.sortDir,
        supplierModel,
        catalogModel,
        openCatalogItemSidebar,
        cloneItem,
        setState,
    ]);

    return columns;
};
